import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import RefundProjectModal from './RefundProjectModal';
import {
  FaDiagramProject,
  FaUser,
  FaUsers,
  FaCalendar,
  FaPen,
  FaCircleCheck,
  FaXmark,
  FaEnvelope,
  FaEye,
  FaClock,
  FaArrowLeft,
  FaArrowUpRightFromSquare,
  FaCreditCard,
  FaUserTie,
  FaFileLines,
  FaStar,
  FaLightbulb,
  FaBullhorn,
  FaCircleQuestion,
} from 'react-icons/fa6';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface UserId {
  _id: string;
  name: string;
  email: string;
  role: string | null;
}

interface ProjectDocument {
  MVP_Scope: { pdf: string | null };
  ICP_Report: { pdf: string | null };
  ICP_Interview_Guide: { pdf: string | null };
  Value_Propositions_Report: { pdf: string | null };
  Competitor_Snapshot: { pdf: string | null };
  GTM_Strategy: { pdf: string | null };
  investorDeck: {
    pdf: string | null;
    ppt: string | null;
    pptStatus: string;
    pptError: string | null;
  };
  financialProjection: {
    summary: string | null;
    excel: string | null;
    inputs: string | null;
    projections: any[];
    generatedAt: string | null;
  };
}

interface StageScores {
  generateCoreQuestion: number | null;
  generateUserPersonas_beforelogin: number | null;
  generateFollowUpQuestion: number | null;
  generatePlatformInformation: number | null;
  generateUserPersonas_afterlogin: number | null;
  generateValueProps: number | null;
  generateFeatures: number | null;
  generateRequirements: number | null;
}

interface Project {
  _id: string;
  projectName: string;
  description: string;
  userId: UserId;
  status: string;
  projectStatus: string;
  isDeleted: boolean;
  document: ProjectDocument;
  stageScores: StageScores;
  qualityScore: number;
  currentDocumentVersion: number;
  platformInfo: Array<{
    platform_name: string;
    platform_type: string;
    user_roles: string[];
    non_technical_details: string;
    technical_details: string;
  }>;
  userPersona: Array<{
    name: string;
    demographics: string[];
    behaviors: string[];
    goals: string[];
    pain_points: string[];
  }>;
  valueProps: Array<{
    persona: string;
    value_proposition: string;
  }>;
  features: Array<{
    name: string;
    priority: string;
    technical_details: string;
    non_technical_details: string;
  }>;
  requirements: Array<{
    domain_name: string;
    functional_requirements: string[];
    non_functional_requirements: string[];
    technical_requirements: string[];
    complexity_assessment: {
      overall_complexity: string;
      reasoning: string;
      risk_factors: string[];
    };
    third_party_services: string[];
  }>;
  coreBusinessQuestion: Array<{
    questionId: string;
    answer: string;
    answerArr: string[];
    _id: string;
  }>;
  followUpQuestion: Array<{
    question: string;
    suggestedAnswer: string;
    answer: string;
    _id: string;
  }>;
  competitors: any[];
  ads: {
    meta?: {
      instagram?: {
        content: string;
        imageUrl: string | null;
        platform: string;
      };
    };
    google?: {
      content: {
        keywords: string[];
        headlines: string[];
        descriptions: string[];
      };
      imageUrl: string | null;
      platform: string;
    };
    x?: {
      content: string;
      imageUrl: string | null;
      platform: string;
    };
  } | null;
  founderKitPaid: boolean;
  founderSessionPaid: boolean;
  refundInfo: {
    isRefunded: boolean;
    amount?: number;
    currency?: string;
    refundedAt?: string;
    stripeRefundId?: string;
  };
  paidAdvisorIds: string[];
  sessionBookings: any[];
  willAnswerFollowUpQuestion: boolean;
  lastDocumentUpdated: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const truncate = (str: string, max = 30) => {
  if (!str) return '—';
  return str.length > max ? str.substring(0, max) + '...' : str;
};

const ADVISOR_NAMES: Record<string, string> = {
  mahesh: 'Mahesh',
  de: 'DE',
};

const DOC_ENTRIES: Array<{ key: keyof ProjectDocument; label: string }> = [
  { key: 'MVP_Scope', label: 'MVP Scope' },
  { key: 'ICP_Report', label: 'ICP Report' },
  { key: 'ICP_Interview_Guide', label: 'ICP Interview Guide' },
  { key: 'Value_Propositions_Report', label: 'Value Propositions Report' },
  { key: 'Competitor_Snapshot', label: 'Competitor Snapshot' },
  { key: 'GTM_Strategy', label: 'GTM Strategy' },
];

const FRONTEND_BASE_URL = 'foundersmate.com';

const getInitials = (name: string) =>
  (name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

const statusMap: Record<string, { cls: string; dot: string }> = {
  published: { cls: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
  active:    { cls: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-400' },
  draft:     { cls: 'bg-gray-100 text-gray-600',       dot: 'bg-gray-400'    },
  deleted:   { cls: 'bg-red-100 text-red-700',         dot: 'bg-red-400'     },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = statusMap[status] || statusMap.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {status}
    </span>
  );
};

const PaidBadge: React.FC<{ paid: boolean }> = ({ paid }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${paid ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
    {paid ? <FaCircleCheck className="w-3 h-3" /> : <FaXmark className="w-3 h-3" />}
    {paid ? 'Paid' : 'Not Paid'}
  </span>
);

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; count?: number; iconBg?: string }> = ({
  icon, title, count, iconBg = 'bg-primary bg-opacity-10',
}) => (
  <div className="flex items-center justify-between mb-5">
    <div className="flex items-center gap-2.5">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBg}`}>
        {icon}
      </div>
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
    </div>
    {count !== undefined && (
      <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{count}</span>
    )}
  </div>
);

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hostedLandingUrl, setHostedLandingUrl] = useState<string>('');
  const [refundModalOpen, setRefundModalOpen] = useState(false);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const response = await api.get(`/admin/projects/${projectId}`);
      return (response.data.data || response.data) as Project;
    },
    enabled: !!projectId,
  });

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    const fetchLandingUrl = async () => {
      try {
        const response = await api.get(`/admin/projects/${projectId}/landing-page`);
        if (!alive) return;
        const responseData = response?.data?.data ?? response?.data;
        const site = Array.isArray(responseData) ? responseData[0] ?? null : responseData;
        const subdomain = site?.subdomain;
        const customDomain = site?.customDomain;
        const url = customDomain
          ? customDomain.startsWith('http') ? customDomain : `https://${customDomain}`
          : subdomain
          ? `https://${subdomain}.${FRONTEND_BASE_URL}`
          : '';
        setHostedLandingUrl(url);
      } catch {
        if (alive) setHostedLandingUrl('');
      }
    };
    fetchLandingUrl();
    return () => { alive = false; };
  }, [projectId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <FaDiagramProject className="w-12 h-12 text-gray-300" />
        <p className="text-gray-500 font-medium">Project not found</p>
        <button onClick={() => navigate('/projects')} className="text-primary text-sm hover:underline">
          Back to Projects
        </button>
      </div>
    );
  }

  const owner = project.userId;
  const teamMembers = [] as any[];
  const userPersonas = Array.isArray(project.userPersona) ? project.userPersona : [];
  const valueProps = Array.isArray(project.valueProps) ? project.valueProps : [];
  const features = Array.isArray(project.features) ? project.features : [];
  const coreQuestions = Array.isArray(project.coreBusinessQuestion) ? project.coreBusinessQuestion : [];
  const followUps = Array.isArray(project.followUpQuestion) ? project.followUpQuestion : [];

  return (
    <>
    <div className="space-y-5 pb-10">

      {/* Back Nav */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/projects')}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
        >
          <FaArrowLeft className="w-4 h-4" />
          Back to Projects
        </button>
      </div>

      {/* Hero Banner */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-primaryDark rounded-2xl shadow-xl"
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full bg-white" />
          <div className="absolute -bottom-16 -left-10 w-80 h-80 rounded-full bg-white" />
        </div>

        <div className="relative p-6 md:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            {/* Icon */}
            <div className="w-16 h-16 rounded-2xl bg-white bg-opacity-20 border-2 border-white border-opacity-30 flex items-center justify-center flex-shrink-0 shadow-lg">
              <FaDiagramProject className="w-7 h-7 text-white" />
            </div>

            {/* Title & Meta */}
            <div className="flex-1 min-w-0">
              <h1
                className="text-2xl md:text-3xl font-bold text-white leading-tight"
                title={project.projectName}
              >
                {truncate(project.projectName, 30)}
              </h1>
              {project.description && (
                <p className="text-blue-100 text-sm mt-1 line-clamp-1">{project.description}</p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <StatusBadge status={project.isDeleted ? 'deleted' : project.status} />
                {project.founderKitPaid && !project.isDeleted && !project.refundInfo?.isRefunded && (
                  <button
                    onClick={() => setRefundModalOpen(true)}
                    className="px-4 py-0.5 text-sm font-medium bg-red-100 text-red-700 rounded-full hover:bg-red-300 transition-colors"
                  >
                    Refund Project
                  </button>
                )}
                {project.refundInfo?.isRefunded && (
                  <span className="px-3 py-1.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                    Refunded {project.refundInfo.refundedAt ? new Date(project.refundInfo.refundedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                  </span>
                )}
                {project.projectStatus && (
                  <span className="inline-flex items-center gap-1 bg-white bg-opacity-15 text-white text-xs font-medium px-2.5 py-0.5 rounded-full border border-white border-opacity-20 capitalize">
                    {project.projectStatus.replace(/_/g, ' ')}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 bg-white bg-opacity-15 text-white text-xs font-medium px-2.5 py-0.5 rounded-full border border-white border-opacity-20">
                  v{project.currentDocumentVersion}
                </span>
                {hostedLandingUrl && (
                  <a
                    href={hostedLandingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 bg-white bg-opacity-15 hover:bg-opacity-25 text-white text-xs font-medium px-2.5 py-0.5 rounded-full border border-white border-opacity-20 transition-colors"
                  >
                    <FaArrowUpRightFromSquare className="w-2.5 h-2.5" />
                    Landing Page
                  </a>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex sm:flex-col gap-5 sm:gap-3 text-center sm:text-right">
              <div>
                <p className="text-2xl font-bold text-white">{features.length}</p>
                <p className="text-blue-200 text-xs">Features</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{userPersonas.length}</p>
                <p className="text-blue-200 text-xs">Personas</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{valueProps.length}</p>
                <p className="text-blue-200 text-xs">Value Props</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Payment & Advisor — 3 column */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
      >
        <SectionHeader
          icon={<FaCreditCard className="w-4 h-4 text-primary" />}
          title="Payment & Advisor Status"
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Founder Kit</p>
            <PaidBadge paid={project.founderKitPaid} />
            <p className="text-xs text-gray-400">One-time document generation package</p>
          </div>

          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Advisor Session</p>
            <PaidBadge paid={project.founderSessionPaid} />
            <p className="text-xs text-gray-400">One-on-one session with an advisor</p>
          </div>

          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Paid Advisors</p>
            {(project.paidAdvisorIds ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {(project.paidAdvisorIds ?? []).map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    <FaUserTie className="w-2.5 h-2.5" />
                    {ADVISOR_NAMES[id] || id}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-xs text-gray-400 italic">No advisors selected</span>
            )}
            <p className="text-xs text-gray-400">Advisors the user has paid to meet</p>
          </div>
        </div>

        {(project.sessionBookings ?? []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Session Bookings</p>
            <div className="space-y-2">
              {(project.sessionBookings ?? []).map((booking: any, i: number) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2.5 text-sm">
                  <span className="text-gray-700 font-medium">{booking.advisorId || `Booking ${i + 1}`}</span>
                  <span className="text-gray-400 text-xs">{formatDate(booking.scheduledAt || booking.createdAt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Owner + Timeline side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Owner */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <SectionHeader
            icon={<FaUser className="w-4 h-4 text-indigo-500" />}
            title="Project Owner"
            iconBg="bg-indigo-50"
          />
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primaryDark flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow">
              {getInitials(owner?.name || '')}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="font-semibold text-gray-900 text-base leading-tight"
                title={owner?.name || ''}
              >
                {truncate(owner?.name || '', 30)}
              </p>
              <p className="text-sm text-gray-400 mt-0.5 flex items-center gap-1">
                <FaEnvelope className="w-3 h-3" />
                {owner?.email || '—'}
              </p>
              <div className="mt-2">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${
                  owner?.role === 'agency_admin' ? 'bg-blue-100 text-blue-700' :
                  owner?.role === 'member' ? 'bg-gray-100 text-gray-600' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  {owner?.role || 'guest'}
                </span>
              </div>
            </div>
            {owner?._id && (
              <button
                onClick={() => navigate(`/users/${owner._id}`)}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primaryDark transition-colors"
              >
                <FaEye className="w-3 h-3" />
                View Profile
              </button>
            )}
          </div>
        </motion.div>

        {/* Timeline */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <SectionHeader
            icon={<FaCalendar className="w-4 h-4 text-purple-500" />}
            title="Timeline"
            iconBg="bg-purple-50"
          />
          <div className="space-y-0 divide-y divide-gray-50">
            {[
              { label: 'Created', value: project.createdAt, icon: <FaCalendar className="w-3.5 h-3.5 text-gray-400" /> },
              { label: 'Last Updated', value: project.updatedAt, icon: <FaClock className="w-3.5 h-3.5 text-gray-400" /> },
              { label: 'Last Document Update', value: project.lastDocumentUpdated, icon: <FaFileLines className="w-3.5 h-3.5 text-gray-400" /> },
            ].map(({ label, value, icon }) => (
              <div key={label} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-2 text-gray-500">
                  {icon}
                  <span className="text-sm">{label}</span>
                </div>
                <span className="text-sm font-medium text-gray-800">{formatDate(value)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-2 text-gray-500">
                <FaXmark className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-sm">Deleted</span>
              </div>
              <span className={`text-sm font-semibold ${project.isDeleted ? 'text-red-600' : 'text-emerald-600'}`}>
                {project.isDeleted ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Description */}
      {project.description && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <SectionHeader
            icon={<FaPen className="w-4 h-4 text-orange-500" />}
            title="Description"
            iconBg="bg-orange-50"
          />
          <p className="text-gray-700 leading-relaxed text-sm">{project.description}</p>
        </motion.div>
      )}

      {/* Generated Documents */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
      >
        <SectionHeader
          icon={<FaFileLines className="w-4 h-4 text-teal-500" />}
          title="Generated Documents"
          iconBg="bg-teal-50"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {DOC_ENTRIES.map(({ key, label }) => {
            const doc = (project.document ?? {})[key] as { pdf: string | null } | undefined;
            const hasPdf = !!doc?.pdf;
            return (
              <div
                key={key}
                className={`rounded-xl border p-3.5 flex items-center justify-between gap-2 ${
                  hasPdf ? 'border-emerald-200 bg-emerald-50' : 'border-gray-100 bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {hasPdf
                    ? <FaCircleCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    : <FaXmark className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                  <span className="text-xs font-medium text-gray-700 leading-tight">{label}</span>
                </div>
                {hasPdf && doc?.pdf && (
                  <a
                    href={doc.pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex-shrink-0 flex items-center gap-0.5"
                  >
                    PDF <FaArrowUpRightFromSquare className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>
            );
          })}

          {/* Investor Deck */}
          {(() => {
            const hasDeck = !!(project.document?.investorDeck?.ppt || project.document?.investorDeck?.pdf);
            return (
              <div className={`rounded-xl border p-3.5 flex items-center justify-between gap-2 ${hasDeck ? 'border-emerald-200 bg-emerald-50' : 'border-gray-100 bg-gray-50'}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {hasDeck
                    ? <FaCircleCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    : <FaXmark className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                  <span className="text-xs font-medium text-gray-700 leading-tight">Two-page Pitch Summary</span>
                </div>
                {project.document?.investorDeck?.ppt && (
                  <a
                    href={project.document.investorDeck.ppt}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex-shrink-0 flex items-center gap-0.5"
                  >
                    PPT <FaArrowUpRightFromSquare className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>
            );
          })()}
        </div>
      </motion.div>

      {/* User Personas */}
      {userPersonas.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <SectionHeader
            icon={<FaUsers className="w-4 h-4 text-blue-500" />}
            title="User Personas"
            count={userPersonas.length}
            iconBg="bg-blue-50"
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {userPersonas.map((persona, i) => (
              <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 text-xs font-bold">
                      {getInitials(persona.name)}
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm" title={persona.name}>
                      {truncate(persona.name, 30)}
                    </h4>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-2 gap-4">
                  {[
                    { label: 'Demographics', items: persona.demographics ?? [], color: 'text-blue-500', dotCls: 'bg-blue-300' },
                    { label: 'Behaviors', items: persona.behaviors ?? [], color: 'text-gray-500', dotCls: 'bg-gray-300' },
                    { label: 'Goals', items: persona.goals ?? [], color: 'text-emerald-500', dotCls: 'bg-emerald-300' },
                    { label: 'Pain Points', items: persona.pain_points ?? [], color: 'text-red-500', dotCls: 'bg-red-300' },
                  ].map(({ label, items, color, dotCls }) => (
                    <div key={label}>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${color}`}>{label}</p>
                      <ul className="space-y-1.5">
                        {items.slice(0, 3).map((item, ii) => (
                          <li key={ii} className="flex items-start gap-1.5 text-xs text-gray-600">
                            <span className={`w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${dotCls}`} />
                            {item}
                          </li>
                        ))}
                        {items.length > 3 && (
                          <li className="text-xs text-gray-400 pl-3">+{items.length - 3} more</li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Value Propositions */}
      {valueProps.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <SectionHeader
            icon={<FaLightbulb className="w-4 h-4 text-yellow-500" />}
            title="Value Propositions"
            count={valueProps.length}
            iconBg="bg-yellow-50"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {valueProps.map((vp, i) => (
              <div key={i} className="border-l-4 border-primary bg-blue-50 rounded-r-xl pl-4 pr-4 py-3">
                <p className="text-xs font-semibold text-primary mb-1" title={vp.persona}>
                  {truncate(vp.persona, 30)}
                </p>
                <p className="text-sm text-gray-700 leading-snug">{vp.value_proposition}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Features */}
      {features.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <SectionHeader
            icon={<FaStar className="w-4 h-4 text-orange-500" />}
            title="Features"
            count={features.length}
            iconBg="bg-orange-50"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {features.map((feature, i) => {
              const priorityCls =
                feature.priority === 'High' ? 'bg-red-100 text-red-700' :
                feature.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-emerald-100 text-emerald-700';
              return (
                <div key={i} className="border border-gray-100 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="font-semibold text-gray-900 text-sm leading-tight" title={feature.name}>
                      {truncate(feature.name, 30)}
                    </p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${priorityCls}`}>
                      {feature.priority}
                    </span>
                  </div>
                  {feature.non_technical_details && (
                    <p className="text-xs text-gray-600 mb-1 leading-snug">{feature.non_technical_details}</p>
                  )}
                  {feature.technical_details && (
                    <p className="text-xs text-gray-400 italic leading-snug">{feature.technical_details}</p>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Core Business Questions */}
      {coreQuestions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <SectionHeader
            icon={<FaDiagramProject className="w-4 h-4 text-violet-500" />}
            title="Core Business Questions"
            count={coreQuestions.length}
            iconBg="bg-violet-50"
          />
          <div className="space-y-3">
            {coreQuestions.map((q, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Answer</p>
                <p className="text-sm font-medium text-gray-900 mb-2">{q.answer}</p>
                {(q.answerArr ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {(q.answerArr ?? []).map((a, ai) => (
                      <span key={ai} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">{a}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Follow-up Questions */}
      {followUps.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <SectionHeader
            icon={<FaCircleQuestion className="w-4 h-4 text-pink-500" />}
            title="Follow-up Questions"
            count={followUps.length}
            iconBg="bg-pink-50"
          />
          <div className="space-y-3">
            {followUps.map((q, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Question</p>
                  <p className="text-sm font-semibold text-gray-900">{q.question}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Answer</p>
                  <p className="text-sm text-gray-700">{q.answer}</p>
                </div>
                {q.suggestedAnswer && q.suggestedAnswer !== q.answer && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Suggested Answer</p>
                    <p className="text-xs text-gray-500 italic">{q.suggestedAnswer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Ad Content */}
      {project.ads && (project.ads.meta?.instagram || project.ads.x || project.ads.google) && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
        >
          <SectionHeader
            icon={<FaBullhorn className="w-4 h-4 text-rose-500" />}
            title="Ad Content"
            iconBg="bg-rose-50"
          />
          <div className="space-y-4">
            {project.ads.meta?.instagram && (
              <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="w-2 h-2 rounded-full bg-pink-500" />
                  <p className="text-xs font-bold text-pink-600 uppercase tracking-wider">Instagram</p>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">{project.ads.meta.instagram.content}</p>
                {project.ads.meta.instagram.imageUrl && (
                  <img src={project.ads.meta.instagram.imageUrl} alt="Instagram ad"
                    className="w-40 h-auto rounded-lg border border-gray-100 shadow-sm" />
                )}
              </div>
            )}
            {project.ads.x && (
              <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="w-2 h-2 rounded-full bg-sky-500" />
                  <p className="text-xs font-bold text-sky-500 uppercase tracking-wider">X (Twitter)</p>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed mb-3">{project.ads.x.content}</p>
                {project.ads.x.imageUrl && (
                  <img src={project.ads.x.imageUrl} alt="X ad"
                    className="w-40 h-auto rounded-lg border border-gray-100 shadow-sm" />
                )}
              </div>
            )}
            {project.ads.google && (
              <div className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Google Ads</p>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Headlines</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(project.ads.google.content.headlines ?? []).map((h, hi) => (
                        <span key={hi} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">{h}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Descriptions</p>
                    {(project.ads.google.content.descriptions ?? []).map((d, di) => (
                      <p key={di} className="text-xs text-gray-700 mb-1 leading-snug">{d}</p>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Keywords</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(project.ads.google.content.keywords ?? []).map((k, ki) => (
                        <span key={ki} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full text-xs border border-blue-100">{k}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

    </div>

    {project && (
      <RefundProjectModal
        isOpen={refundModalOpen}
        projectId={project._id}
        projectName={project.projectName}
        userEmail={project.userId?.email || ''}
        amountCents={project.refundInfo?.amount ?? 0}
        currency={project.refundInfo?.currency ?? 'usd'}
        onClose={() => setRefundModalOpen(false)}
        onSuccess={() => {
          setRefundModalOpen(false);
          toast.success('Refund processed and project deleted');
          queryClient.invalidateQueries({ queryKey: ['project', projectId] });
        }}
      />
    )}
    </>
  );
};

export default ProjectDetail;
